import React, { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth, functions } from '../firebase/firebaseConfig';
import PhoneInput from '../components/PhoneInput';
import { TfrxHeader, TfrxFooter } from '../components/TfrxChrome';

// Self-service account deletion (Google Play "Data deletion" URL).
//   phone → warning → send OTP → verify → custom-token sign-in → deleteAccount
// Reuses the exact same Cloud Functions the mobile app uses, so the server-side
// cleanup (Auth + Firestore + Storage) is identical and never trusts the client.
const STEP_PHONE   = 'phone';
const STEP_CONFIRM = 'confirm';
const STEP_OTP     = 'otp';
const STEP_DONE    = 'done';

const RED  = '#E63946';
const GOLD = '#D4B574';

export default function DeleteAccountPage() {
  const [step,  setStep]  = useState(STEP_PHONE);
  const [phone, setPhone] = useState('+91');
  const [otp,   setOtp]   = useState('');
  const [error, setError] = useState(null);
  const [busy,  setBusy]  = useState(false);

  const validPhone = /^\+91\d{10}$/.test(phone.trim());

  // Step 1 → show the permanent-deletion warning.
  const goToConfirm = useCallback((e) => {
    e?.preventDefault?.();
    setError(null);
    if (!validPhone) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    setStep(STEP_CONFIRM);
  }, [validPhone]);

  // Step 2 (Yes) → send the OTP to the registered number.
  const sendOtp = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await httpsCallable(functions, 'sendMsg91Otp')({ phone: phone.trim() });
      setStep(STEP_OTP);
    } catch (err) {
      setError(err?.message || 'Could not send the OTP. Please try again.');
      setStep(STEP_PHONE);
    } finally {
      setBusy(false);
    }
  }, [phone]);

  const resendOtp = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      await httpsCallable(functions, 'resendMsg91Otp')({ phone: phone.trim() });
    } catch (err) {
      setError(err?.message || 'Could not resend the OTP.');
    } finally {
      setBusy(false);
    }
  }, [phone]);

  // Step 3 → verify OTP, sign in with the returned custom token, then delete.
  const verifyAndDelete = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!/^\d{4,6}$/.test(otp.trim())) {
      setError('Enter the code from the SMS.');
      return;
    }
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'verifyMsg91Otp')({
        phone: phone.trim(),
        otp:   otp.trim(),
      });
      const token = res?.data?.token;
      if (!token) throw new Error('Verification failed. Please try again.');

      // Sign in as this member, then let deleteAccount run under their identity
      // (the function reads request.auth.uid / token — never a client value).
      await signInWithCustomToken(auth, token);
      await httpsCallable(functions, 'deleteAccount')();
      await signOut(auth).catch(() => {});
      setStep(STEP_DONE);
    } catch (err) {
      setError(err?.message || 'Deletion failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [phone, otp]);

  const wrap = (children) => (
    <div style={{
      minHeight: '100vh', background: '#09090B',
      display: 'flex', flexDirection: 'column',
    }}>
      <TfrxHeader />
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 16px',
      }}>
        <div style={{
          maxWidth: 480, width: '100%',
          background: '#111113',
          border: '1px solid rgba(230, 57, 70, 0.25)',
          borderRadius: 16,
          padding: 28,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 80px rgba(230,57,70,0.06)',
        }}>{children}</div>
      </div>
      <TfrxFooter />
    </div>
  );

  const errorBox = error && (
    <div style={{
      marginBottom: 16, padding: '10px 14px',
      background: 'rgba(230,57,70,0.12)',
      border: `1px solid ${RED}`,
      borderRadius: 8, color: RED, fontSize: 13,
    }}>{error}</div>
  );

  // DONE
  if (step === STEP_DONE) return wrap(
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
      <h2 style={{ color: '#F7E8BF', fontSize: 22, fontWeight: 900, marginBottom: 10 }}>
        Account deleted
      </h2>
      <p style={{ color: '#D8C8A4', fontSize: 14, lineHeight: 1.7 }}>
        Your Team Fronx account and all associated data — membership, profile,
        vehicle details, photos and applications — have been permanently
        removed. This cannot be undone.
      </p>
      <p style={{ color: '#B79F6D', fontSize: 12, marginTop: 14 }}>
        You’re welcome back any time via the app’s sign-up.
      </p>
    </div>
  );

  // OTP
  if (step === STEP_OTP) return wrap(
    <form onSubmit={verifyAndDelete}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <img src="/logo.png" alt="" style={{ width: 56, height: 56, marginBottom: 8 }} />
        <h2 style={{ color: '#F7E8BF', fontSize: 20, fontWeight: 900, marginBottom: 4 }}>
          Enter verification code
        </h2>
        <p style={{ color: '#B79F6D', fontSize: 13 }}>
          Sent to {phone.trim()}
        </p>
      </div>

      {errorBox}

      <input
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="______"
        style={{
          width: '100%', padding: '14px', textAlign: 'center',
          background: '#0F0F0F', border: '1px solid rgba(212, 181, 116, 0.3)',
          borderRadius: 10, color: '#F7E8BF',
          fontSize: 24, fontWeight: 800, letterSpacing: 8,
          fontFamily: 'monospace', boxSizing: 'border-box',
        }}
      />

      <button type="submit" disabled={busy}
        style={{
          width: '100%', padding: '14px', marginTop: 16,
          background: busy ? '#5A1F24' : RED,
          color: '#fff', border: 'none', borderRadius: 12,
          fontSize: 15, fontWeight: 900, letterSpacing: 0.5,
          cursor: busy ? 'wait' : 'pointer',
        }}>
        {busy ? 'Deleting…' : 'Permanently delete my account'}
      </button>

      <button type="button" onClick={resendOtp} disabled={busy}
        style={{
          width: '100%', padding: '10px', marginTop: 8,
          background: 'transparent', color: '#B79F6D',
          border: 'none', fontSize: 12, cursor: busy ? 'wait' : 'pointer',
        }}>
        Didn’t get the code? Resend
      </button>
    </form>
  );

  // CONFIRM (warning)
  if (step === STEP_CONFIRM) return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>⚠️</div>
        <h2 style={{ color: RED, fontSize: 21, fontWeight: 900 }}>
          Delete your account?
        </h2>
      </div>

      {errorBox}

      <div style={{
        padding: 16, marginBottom: 18,
        background: 'rgba(230,57,70,0.08)',
        border: `1px solid rgba(230,57,70,0.3)`,
        borderRadius: 10, color: '#D8C8A4', fontSize: 13.5, lineHeight: 1.7,
      }}>
        This <strong style={{ color: '#F7E8BF' }}>permanently deletes</strong> the
        account for <strong style={{ color: '#F7E8BF' }}>{phone.trim()}</strong> and
        cannot be undone. You will lose:
        <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
          <li>Your membership and member ID</li>
          <li>Your profile, vehicle details and photos</li>
          <li>All membership / renewal applications</li>
          <li>App access — immediately</li>
        </ul>
      </div>

      <button onClick={sendOtp} disabled={busy}
        style={{
          width: '100%', padding: '14px',
          background: busy ? '#5A1F24' : RED,
          color: '#fff', border: 'none', borderRadius: 12,
          fontSize: 15, fontWeight: 900, letterSpacing: 0.5,
          cursor: busy ? 'wait' : 'pointer',
        }}>
        {busy ? 'Sending OTP…' : 'Yes, send me an OTP to confirm'}
      </button>

      <button onClick={() => { setStep(STEP_PHONE); setError(null); }} disabled={busy}
        style={{
          width: '100%', padding: '12px', marginTop: 8,
          background: 'transparent', color: '#B79F6D',
          border: 'none', fontSize: 13, cursor: 'pointer',
        }}>
        Cancel — keep my account
      </button>
    </>
  );

  // PHONE
  return wrap(
    <form onSubmit={goToConfirm}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <img src="/logo.png" alt="" style={{ width: 60, height: 60, marginBottom: 6 }} />
        <h1 style={{ color: '#F7E8BF', fontSize: 22, fontWeight: 900, marginBottom: 4 }}>
          Delete Account
        </h1>
        <p style={{ color: '#B79F6D', fontSize: 12, letterSpacing: 1, lineHeight: 1.5 }}>
          Request permanent deletion of your Team Fronx account and data
        </p>
      </div>

      {errorBox}

      <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
        Your registered mobile number
      </label>
      <div style={{ marginTop: 6 }}>
        <PhoneInput value={phone} onChange={setPhone} />
      </div>

      <p style={{ color: '#7A7A7A', fontSize: 11.5, lineHeight: 1.6, margin: '12px 0 0' }}>
        We’ll send a one-time code to this number to confirm it’s you before
        anything is deleted. Only the account belonging to this number can be
        removed here.
      </p>

      <button type="submit" disabled={!validPhone}
        style={{
          width: '100%', padding: '14px', marginTop: 16,
          background: validPhone ? RED : '#3A2224',
          color: '#fff', border: 'none', borderRadius: 12,
          fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
          cursor: validPhone ? 'pointer' : 'not-allowed',
        }}>
        Continue
      </button>

      <p style={{ color: '#666', fontSize: 11, textAlign: 'center', marginTop: 14, lineHeight: 1.6 }}>
        Prefer help? Email{' '}
        <a href="mailto:support@teamfronx.com" style={{ color: GOLD }}>
          support@teamfronx.com
        </a>
      </p>
    </form>
  );
}
